import { action, computed, makeObservable, observable } from 'mobx';

import { ENDPOINTS } from 'config/endpoints';
import {
  AuthorizationState,
  normalizeUserEntity,
  UserEntity,
  UserEntityApi,
} from 'entities/User';
import {
  UserLoginRequest,
  UserLoginResponse,
} from 'entities/endpoints/user.login';
import { ApiRequest } from 'stores/common';
import { ILocalStore } from 'stores/interfaces';
import { printErrors } from 'utils/printErrors';

type PrivateFields = '_userData' | '_authorizationStatus';

export default class UserStore implements ILocalStore {
  constructor() {
    makeObservable<UserStore, PrivateFields>(this, {
      _userData: observable.ref,
      setUserData: action.bound,
      user: computed,

      _authorizationStatus: observable,
      isAuthorized: computed,
      isAuthorizing: computed,
      logoutLoading: computed,

      setAuthorized: action.bound,
      loadUser: action.bound,
      login: action.bound,
      logout: action.bound,
    });
  }

  private _userData: UserEntity | null = null;
  private _authorizationStatus: AuthorizationState =
    AuthorizationState.authorizing;

  private readonly _currentUserRequest = new ApiRequest({
    endpoint: ENDPOINTS.user.current,
    methodType: 'GET',
  });

  readonly loginRequest = new ApiRequest({
    endpoint: ENDPOINTS.user.login,
    methodType: 'POST',
  });

  private readonly _logoutUserRequest = new ApiRequest({
    endpoint: ENDPOINTS.user.logout,
    methodType: 'POST',
  });

  get user(): UserEntity | null {
    return this._userData;
  }

  get isAuthorized(): boolean {
    return this._authorizationStatus === AuthorizationState.authorized;
  }

  get isAuthorizing(): boolean {
    return this._authorizationStatus === AuthorizationState.authorizing;
  }

  get logoutLoading(): boolean {
    return this._logoutUserRequest.loading;
  }

  setUserData(data: UserEntity | null): void {
    this._userData = data;
  }

  setAuthorized(value: AuthorizationState): void {
    this._authorizationStatus = value;
  }

  /**
   * Метод для загрузки текущего пользователя
   */
  async loadUser(): Promise<void> {
    this.setAuthorized(AuthorizationState.authorizing);

    try {
      const { data, isError } =
        await this._currentUserRequest.call<UserEntityApi>();

      if (!isError && data) {
        this.setAuthorized(AuthorizationState.authorized);
        this.setUserData(normalizeUserEntity(data));

        return;
      }
    } catch (e) {
      printErrors(e);
    }

    this.setAuthorized(AuthorizationState.unauthorized);
  }

  async login(params: UserLoginRequest): Promise<void> {
    const { isError, data } = await this.loginRequest.call<
      UserLoginResponse,
      UserLoginRequest
    >({
      data: params,
    });

    if (!isError && data) {
      this.setUserData(normalizeUserEntity(data));
      this.setAuthorized(AuthorizationState.authorized);
    }
  }

  async logout(): Promise<void> {
    const { isError } = await this._logoutUserRequest.call();

    if (isError) {
      return;
    }

    this.setAuthorized(AuthorizationState.unauthorized);
    this.setUserData(null);
  }

  destroy() {
    this._currentUserRequest.destroy();
    this.loginRequest.destroy();
    this._logoutUserRequest.destroy();
  }
}
